import * as Sentry from "@sentry/react-native";
import { QueryClientProvider } from "@tanstack/react-query";
import { Slot, useGlobalSearchParams, usePathname } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, RawIntlProvider } from "react-intl";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { AlertProvider } from "~/components/AlertProvider/AlertProvider";
import { Button } from "~/components/Button";
import { ModalProvider } from "~/components/ModalProvider";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { ErrorAlertProviderV2 } from "~/components/V2/error-alert-v2";
import { View } from "~/components/View";
import { ErrorAlertProvider } from "~/components/error-alert";
import { logEvent } from "~/features/analytics/firebaseAnalytics";
import { WithClinicPhoneNumber } from "~/hoc/WithClinicPhoneNumber";
import { initialize } from "~/initialize";
import { MSTProvider } from "~/mobx/MSTProvider";
import type { RootStoreInstance } from "~/mobx/RootStore";
import RNRestart from "~/utils/restart-app";
import { WithSplashScreen } from "../features/splash/WithSplashScreen";
import type { QueryClient } from "@tanstack/react-query";

SplashScreen.preventAutoHideAsync();

// Set the animation options. This is optional.
SplashScreen.setOptions({
  duration: 1000,
  fade: true,
});

export default function RootLayout() {
  //#region Analytics
  const pathname = usePathname();
  const params = useGlobalSearchParams();

  // Track the location in your analytics provider here.
  useEffect(() => {
    logEvent("screen_view", {
      path_name: pathname,
      ...params,
    });
  }, [pathname, params]);
  //#endregion Analytics

  const [isReady, setIsReady] = useState(false);
  const [initializationError, setInitializationError] = useState<
    Error | undefined
  >(undefined);
  const store = useRef<RootStoreInstance | undefined>(undefined);
  const queryClient = useRef<QueryClient | undefined>(undefined);

  useEffect(() => {
    if (isReady) {
      SplashScreen.hideAsync();
    }
  }, [isReady]);

  useEffect(() => {
    initialize()
      .then((context) => {
        store.current = context.store;
        queryClient.current = context.queryClient;

        setIsReady(true);
      })
      .catch((error) => {
        console.log("Error initializing the app", error);
        Sentry.captureException(error);
        setInitializationError(error);
      });
  }, [setInitializationError]);

  if (initializationError) {
    return (
      <RawIntlProvider value={intl}>
        <Screen withTopInset={false} withBottomInset>
          <View flex centerContent paddingExtraLarge>
            <Text sizeLarge alignCenter>
              <FormattedMessage
                id="uFmv01"
                defaultMessage="Something went wrong, please try again later."
                description="Error message when the app fails to initialize"
              />
            </Text>
            <Spacer extraLarge />
            <Button
              outline
              title={intl.formatMessage({
                id: "WWEZX6",
                defaultMessage: "Try Again",
                description: "Button to restart the app",
              })}
              onPress={() => {
                RNRestart.Restart();
              }}
            />
          </View>
        </Screen>
      </RawIntlProvider>
    );
  }

  return (
    <WithSplashScreen isAppReady={isReady}>
      <StatusBar style="light" />
      {isReady && (
        <>
          <RawIntlProvider value={intl}>
            <SafeAreaProvider>
              <ModalProvider>
                <AlertProvider>
                  <ErrorAlertProvider>
                    <ErrorAlertProviderV2>
                      <MSTProvider store={store.current}>
                        <QueryClientProvider
                          client={queryClient.current as QueryClient}
                        >
                          <WithClinicPhoneNumber>
                            <Slot />
                            {/* Slot will render the current child route */}
                          </WithClinicPhoneNumber>
                        </QueryClientProvider>
                      </MSTProvider>
                    </ErrorAlertProviderV2>
                  </ErrorAlertProvider>
                </AlertProvider>
              </ModalProvider>
            </SafeAreaProvider>
          </RawIntlProvider>
        </>
      )}
    </WithSplashScreen>
  );
}

export {
  // Catch any errors thrown by the Layout component.
  // This is a default ErrorBoundary component from Expo Router.
  // we use it for convenience
  ErrorBoundary,
} from "expo-router";
